import { PUSH_TO_TALK_MIN_SECONDS } from '@/_data/voiceInput';
import { chatInputBlockReason, type createAgentChatController } from './agentChatController';
import type { createVoiceInputController } from './voiceInputController';

type Voice = Pick<ReturnType<typeof createVoiceInputController>, 'getSnapshot' | 'subscribe' | 'record' | 'stop' | 'cancel' | 'transcribe'>;
type Chat = Pick<ReturnType<typeof createAgentChatController>, 'getSnapshot' | 'send'>;

/**
 * Hold to record, release to transcribe and send. The transcript joins any existing draft, and the whole draft is sent.
 * Failures and empty transcripts are reported through `onProblem`, leaving the draft as it was.
 */
export function createPushToTalk(voice: Voice, chat: Chat, onProblem: (message: string) => void) {
    let active = false;
    let pending = false;
    let generation = 0;
    let previousResult = voice.getSnapshot().result;
    const settle = () => {
        const state = voice.getSnapshot();
        if ((active || pending) && state.phase === 'error') {
            active = false;
            pending = false;
            onProblem(state.error ?? 'Voice input failed. Press Y to inspect the Agent panel.');
            return;
        }
        if (!pending) { previousResult = state.result; return; }
        if (state.phase === 'ready') { void voice.transcribe(); return; }
        if (state.phase !== 'idle') return;
        pending = false;
        const result = state.result;
        if (result === previousResult) return;
        previousResult = result;
        if (result?.text.trim()) void chat.send();
        else onProblem('Nothing heard - hold X and speak');
    };
    const unsubscribe = voice.subscribe(settle);
    const cancel = () => {
        if (!active && !pending) return;
        active = false;
        pending = false;
        generation++;
        voice.cancel();
    };
    return {
        /** Starts recording; returns why it cannot, if so. */
        press(): string | undefined {
            const state = voice.getSnapshot();
            if (state.configuration === 'loading') return 'Checking voice input availability. Try X again shortly.';
            if (!state.enabled) return state.error ?? 'Voice input is disabled on this server. Press Y to inspect the Agent panel.';
            // A failed earlier attempt still holds the draft reservation; a new press replaces it.
            if (state.phase === 'error') voice.cancel();
            const phase = voice.getSnapshot().phase;
            if (phase !== 'idle') return phase === 'requesting' ? 'Microphone permission is still opening. Allow microphone access and keep X held.'
                : phase === 'transcribing' ? 'Your previous recording is still transcribing. Wait, or hold X + left trigger to cancel.'
                    : phase === 'recording' ? 'A recording is already in progress. Finish it or cancel voice input in the Agent panel (Y).'
                        : phase === 'stopping' ? 'Finishing the recording. Wait for transcription.'
                            : 'A recording is waiting for transcription. Transcribe or cancel it in the Agent panel (Y).';
            const blocked = chatInputBlockReason(chat.getSnapshot());
            if (blocked) return `${blocked} Y opens the Agent panel.`;
            pending = false;
            active = true;
            const attempt = ++generation;
            previousResult = state.result;
            void voice.record().catch(() => {
                if (attempt !== generation || !active && !pending) return;
                cancel();
                onProblem('Could not start recording. Press Y to inspect microphone access and retry.');
            });
            return undefined;
        },
        release(heldSeconds: number) {
            if (!active) return;
            const state = voice.getSnapshot();
            if (state.phase === 'requesting') {
                cancel();
                onProblem('Microphone was not ready when X was released. Allow microphone access, then hold X until Listening appears.');
                return;
            }
            if (heldSeconds < PUSH_TO_TALK_MIN_SECONDS) {
                cancel();
                onProblem(`Recording discarded: hold X for at least ${PUSH_TO_TALK_MIN_SECONDS} seconds while speaking, then release.`);
                return;
            }
            if (!['recording', 'stopping', 'ready'].includes(state.phase)) {
                cancel();
                onProblem(state.error ?? 'Recording did not start. Press Y to inspect microphone access and retry.');
                return;
            }
            active = false;
            pending = true;
            if (state.phase === 'recording') voice.stop();
            else settle();
        },
        /** Discards the recording or transcription in progress, if this push-to-talk started it. */
        cancel,
        /** True from press until the message is sent, cancelled or fails. */
        busy: () => active || pending,
        dispose: unsubscribe,
    };
}
